import { observer } from "mobx-react-lite";
import { useCallback } from "react";
import { RecallPermissionKind } from "@/modules/electron/types";
import { electronModule } from "@/modules/electron";
import { useAppStore } from "@/store/utils/hooks";
import {
  useSystemAudioPermissionSynchronizer,
  useMeetingDetectionListener,
} from "./system-permissions-manager.hooks";
import { MeetingPermissionsModal } from "@/components/desktop/system-permissions/meetings/meetings-permissions-modal";
import { VoicePermissionsModal } from "@/components/desktop/system-permissions/voice/voice-permissions-modal";

export const SystemPermissionsManager = observer(() => {
  const { store } = useAppStore();
  const systemPermissionsUi = store.systemPermissions.ui;

  useSystemAudioPermissionSynchronizer();
  useMeetingDetectionListener();

  const handleRequestPermission = useCallback(async (permission: RecallPermissionKind) => {
    await electronModule.recall.requestPermission({ permission });
  }, []);

  const handleOpenPermissionPane = useCallback(async (permission: RecallPermissionKind) => {
    await electronModule.recall.openPermissionPane({ permission });
  }, []);

  const handleRestart = useCallback(async () => {
    if (electronModule.restartApp) {
      await electronModule.restartApp();
      return;
    }

    await electronModule.closeWindow();
  }, []);

  const onChangeDetectionOfUnscheduledMeetings = useCallback(
    (checked: boolean) => {
      store.systemPermissions.setMeetingNotificationEnabled({ enabled: checked });
    },
    [store.systemPermissions]
  );

  const onChangeRemindMeToJoinMeetingsAndTakeNotes = useCallback(
    (checked: boolean) => {
      store.systemPermissions.setCalendarNotificationEnabled({ enabled: checked });
    },
    [store.systemPermissions]
  );

  return (
    <>
      <VoicePermissionsModal
        isOpen={systemPermissionsUi.isVoiceModalOpen}
        onClose={systemPermissionsUi.closeModal}
        permissionStatusMap={store.systemPermissions.permissionMap}
        onRequestPermission={handleRequestPermission}
        onOpenPermissionPane={handleOpenPermissionPane}
        onRestart={handleRestart}
      />
      {/**
       * We reuse the same modal, with the `advancedMeetingPermissionsRequired` flagged on or off.
       */}
      <MeetingPermissionsModal
        platform={{
          isMacOs: store.interface.isMacOs,
          isIntelMacDesktop: store.electron.isIntelMacDesktop,
        }}
        isOpen={
          systemPermissionsUi.isMeetingModalOpen || systemPermissionsUi.isAdvancedMeetingModalOpen
        }
        onClose={systemPermissionsUi.closeModal}
        permissionStatusMap={store.systemPermissions.permissionMap}
        advancedMeetingPermissionsRequired={!!systemPermissionsUi.isAdvancedMeetingModalOpen}
        onRequestPermission={handleRequestPermission}
        onOpenPermissionPane={handleOpenPermissionPane}
        onRestart={handleRestart}
        isDetectionOfUnscheduledMeetingsEnabled={store.systemPermissions.meetingNotificationEnabled}
        onChangeDetectionOfUnscheduledMeetings={onChangeDetectionOfUnscheduledMeetings}
        isRemindMeToJoinMeetingsAndTakeNotesEnabled={
          store.systemPermissions.calendarNotificationEnabled
        }
        onChangeRemindMeToJoinMeetingsAndTakeNotes={onChangeRemindMeToJoinMeetingsAndTakeNotes}
      />
    </>
  );
});
